
import { PlayerObject } from "@models/UpsertableBase";
import {
  Feature,
  SmartHash,
} from "@models";
import { 
  CharacterClass, 
  CharacterBackground,
  CharacterASIBaseFeature,
  CharacterRace,
  CharacterItem,
  CharacterFeat,
  CharacterAbility, 
  CharacterSpell, 
  CharacterSlot,
  CharacterSense,
  CharacterGift,
  CharacterAction,
  CharacterArmor,
  CharacterAmmunition,
  CharacterBasicItem,
  CharacterContainer,
  CharacterWeapon,
  CharacterCustomItem,
  CharacterFeatureBase,
  COptions,
  AbilityScores,
  HitDiceWithUsed, 
  DamageMultiplierSimple,
  Bonus,
  Reroll,
  Modifier,
  CharacterFeature,
  CharacterProficiency,
  CharacterAdvantage,
  RollPlus,
  CharacterFeatureSource,
  MinimumRoll,
  SkillAbilityOverrideFeature,
  UpgradableNumber,
  CharacterExtraEffects,
  CharacterDeck,
  CharacterGem,
  AlternateConditionFeature,
  Creature,
  SizeOptions,
  CharacterResourceHolder,
  CharacterSubscriptionHolder,
  GreaterAdvantage,
  CharacterBook,
  CreatureHP,
  CharacterStandardFeatures,
  CharacterAbilityModifier,
  CharacterBastionFacility,
  CharacterCondition,
  CampaignChar,
  CampaignNPC,
  Spirit,
  Mind,
  Body,
  StoredSpell,
  Receivable,
  CharacterSheetInterface,
  CharacterWeaponMastery,
  CharacterWeaponMasteryMaster,
  Armor,
  WeaponKeywordUpgradeFeature,
  WeaponKeywordUpgradeFeature2,
  CharacterSpecialFeature,
  CharacterEldritchInvocation,
  CharacterFightingStyle,
  CharacterPactBoon,
  ActionType,
  ActionTypeUpgrade,
  CharacterConditionFeature,
  Money,
  Ability,
  ExtraEffectsFeature,
  CreatureInstance,
  TrueCreatureInstance,
  TableEntry,
  KnowledgeFeature,
  Attributes,
  // SpellsFromList,
  // SpellsFromListWithCosts,
} from "@dndModels";
import { CharacterKnowledge } from "@dndModels/character_feature_source/CharacterKnowledge";
import { AbilityScoreType, AttackType, CreatureType, DamageType, DataType, ModType, SizeType } from "@dndModels/Types";
import CommonUtilities from "@utilities/common_utilities";
import { SIZE_MAP } from "@dndModels/Constants";

export interface TurnResourceNumber {
  used: number;
  total: number;
}

export interface TurnResources {
  A: TurnResourceNumber, BA: TurnResourceNumber, RA: TurnResourceNumber, Attack: TurnResourceNumber, LAttack: TurnResourceNumber
}

const common_util = CommonUtilities.getInstance();

export class Character extends PlayerObject {
  data_type: DataType = "character";
  subscription_holder: CharacterSubscriptionHolder = new CharacterSubscriptionHolder();
  archived_data_holder: SmartHash = {};

  spirit = new CharacterSpirit();
  mind = new CharacterMind();
  body = new CharacterBody();
  inventory = new CharacterInventory();
  notes = new CharacterNotes();
  character_options = new CharacterOptions();
  customizations = new CharacterCustomizations();
  show_hidden = false;
  show_extra_action_types = false;
  show_rituals = false;

  receivables: Receivable[] = [];

  get char_feature_sources(): CharacterFeatureSource[] {
    let char_feature_sources: CharacterFeatureSource[] = [
      ...this.spirit.char_feature_sources,
      ...this.inventory.char_feature_sources
    ];
    let i = 0;
    while (i < char_feature_sources.length) {
      const cfs = char_feature_sources[i];
      char_feature_sources = [...char_feature_sources,...cfs.sub_char_feature_sources];
      ++i;
    }
    return char_feature_sources;
  }
  
  shared_id = "";
  piece_version = 3;
  bg_image_url = "";
  connected = false;
  last_campaign_update = 0;
  old_campaign_id = "";
  saved_ac = 0; // This is so that we can display the last known AC for it in Encounters without loading the full thing and processing.

  last_saved = 0;
  cookie_saved = 0; 
  last_changed = 0;
  dont_auto_save = false;

  c_char?: CampaignChar;
  c_npc?: CampaignNPC;

  get campaign() {
    if (this.c_char) {
      return this.c_char.campaign;
    } else if (this.c_npc) {
      return this.c_npc.campaign;
    }
    return undefined;
  }

  changed() {
    this.last_changed = common_util.get_time_number();
    this.subscription_holder.characterChanged();
  }
  get changed_since_saved() {
    return this.last_changed > this.last_saved || this.cookie_saved > this.last_saved;
  }
  get changed_since_cookie_saved() {
    return this.last_changed > this.cookie_saved;
  }
  get cookie_outdated() {
    return this.last_saved > this.cookie_saved;
  }

  use_transform = true;

  get char_interface(): CharacterSheetInterface  {
    if (this.use_transform && this.spirit.transform) {
      return this.spirit.transform;
    }
    return this;
  }

  get attributes() {
    const attributes = new Attributes();
    attributes.damage_immunities = this.damage_multipliers.filter(o => o.multiplier === 0 && o.damage_type !== "").map(o => o.damage_type) as DamageType[]; // : DamageType[] = []; 
    attributes.damage_resistances = this.damage_multipliers.filter(o => o.multiplier === 0.5 && o.damage_type !== "").map(o => o.damage_type) as DamageType[]; // : DamageType[] = [];
    attributes.damage_vulnerabilities = this.damage_multipliers.filter(o => o.multiplier === 2 && o.damage_type !== "").map(o => o.damage_type) as DamageType[]; // : DamageType[] = [];
    attributes.extra_damage_immunities = "";
    attributes.extra_damage_resistances = "";
    attributes.extra_damage_vulnerabilities = "";
    // attributes.condition_immunities: string[] = [];
    attributes.senses = {};
    for (const sense of this.senses) {
      attributes.senses[sense.name] = sense.range;
    }
    attributes.skill_profs = {};
    for (const skill of this.proficiencies.filter(o => o.type === "Skill")) {
      attributes.skill_profs[skill.proficiency_id] = skill.proficiency_level;
    }
    attributes.save_profs = this.saving_throw_proficiencies.map(o => o.proficiency_id);
    attributes.speeds = this.speed;
    attributes.hover = false;
    attributes.tool_profs = [];
    attributes.languages = "";
    return attributes;
  }

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.piece_version = obj.piece_version ? obj.piece_version : 3;
      this.shared_id = obj.shared_id ? obj.shared_id : "";
      this.last_saved = obj.last_saved ? obj.last_saved : 0;
      this.cookie_saved = obj.cookie_saved ? obj.cookie_saved : 0;
      this.bg_image_url = obj.bg_image_url ? obj.bg_image_url : "";

      this.saved_race_and_class_string = obj.saved_race_and_class_string ? obj.saved_race_and_class_string : "";
      this.saved_race_string = obj.saved_race_string ? obj.saved_race_string : "";
      this.saved_class_string = obj.saved_class_string ? obj.saved_class_string : "";
      this.last_campaign_update = obj.last_campaign_update ? obj.last_campaign_update : 0;
      this.old_campaign_id = obj.old_campaign_id ? obj.old_campaign_id : "";
      this.saved_ac = obj.saved_ac ? obj.saved_ac : 0;
    }
  }

  get_detail_format() {
    let str = `${this.race_and_class_string}`;
    if (this.owner) {
      str +=  ` - ${ this.owner.use_name }`;
    }
    return str.trim();
  }

  apply_piece(piece_name: string, piece: any) {
    if (this.piece_version === 1) {
      if (!piece.message && piece) {
        // switch(piece_name) {
        //   case "main": // This piece is the main one, is already applied before this.
        //   break;
        //   case "abilities": 
        //     // char_id: this._id,
        //     if (this.character_options.ability_score_gen_method === "Any" || 
        //       this.character_options.ability_score_gen_method === "Not Manual/Rolled") {
        //       if (piece.ability_score_gen_method) {
        //         if (this.character_options.ability_score_gen_method === "Any" || 
        //           (this.character_options.ability_score_gen_method === "Not Manual/Rolled" && 
        //             piece.ability_score_gen_method !== "Manual")) {
        //           this.ability_score_gen_method = piece.ability_score_gen_method;
        //         }
        //       }
        //     } else {
        //       this.ability_score_gen_method = this.character_options.ability_score_gen_method;
        //     }
        //     this.base_ability_scores = piece ? new AbilityScores(piece.base_ability_scores) : new AbilityScores();
        //   break;
        //   case "features":
        //     this.extra_damage_multipliers = [];
        //     if (piece && piece.extra_damage_multipliers) {
        //       for (let i = 0; i < piece.extra_damage_multipliers.length; i++) {
        //         const dm = new DamageMultiplierSimple(piece.extra_damage_multipliers[i]);
        //         this.extra_damage_multipliers.push(dm);
        //       }
        //     }
        //     this.favorite_conditions = piece ? piece.favorite_conditions : [];
        //     this.conditions = [];
        //     if (piece && piece.conditions) {
        //       for (let i = 0; i < piece.conditions.length; i++) {
        //         const cc = new CharacterCondition(piece.conditions[i]);
        //         if (cc.source_id === "") {
        //           return "condition";
        //         }
        //         this.conditions.push(cc);
        //       }
        //     } else if (piece && piece.condition_hash) {
        //       const cond_ids = Object.keys(piece.condition_hash);
        //       for (const cond_id of cond_ids) {
        //         if (typeof piece.condition_hash[cond_id] === "object") {
        //           // this.condition_hash[cond_id] = piece.condition_hash[cond_id];
        //           const cc = new CharacterCondition();
        //           cc.source_id = cond_id;
        //           cc.level = piece.condition_hash[cond_id].level;
        //           cc.condition_source_true_id = piece.condition_hash[cond_id].source_true_id;
        //           this.conditions.push(cc);
        //         } else if (typeof piece.condition_hash[cond_id] === "number") {
        //           // this.condition_hash[cond_id] = { level: piece.condition_hash[cond_id], source_true_id: "" };
        //           const cc = new CharacterCondition();
        //           cc.source_id = cond_id;
        //           cc.level = piece.condition_hash[cond_id];
        //           this.conditions.push(cc);
        //         }
        //       }
        //     }
        //     this.condition_immunities = piece ? piece.condition_immunities : [];
        //     this.advantage = piece ? +piece.advantage : 0;
        //     this.concentrating_on = null;
        //     if (piece && piece.concentrating_on) {
        //       if (piece.concentrating_on.ability_type) {
        //         this.concentrating_on = new CharacterAbility(piece.concentrating_on);
        //         const source_id = this.concentrating_on.source_id;
        //         if (this.concentrating_on.source_type === "Class") {
        //           const class_finder = this.classes.filter(o => o.source_id === source_id);
        //           if (class_finder.length === 1) {
        //             this.concentrating_on.connectSource(class_finder[0]);
        //           }
        //         }
        //       } else if (piece.concentrating_on.spell_id) {
        //         this.concentrating_on = new CharacterSpell(piece.concentrating_on);
        //       }
        //     }
        //     this.minions = [];
        //     if (piece && piece.minions && piece.minions.length > 0) {
        //       // console.log(piece.minions);
        //       for (const ci of piece.minions) {
        //         // debugger;
        //         if (ci.instance_type === undefined && ci.transform_id && ci.transform_id !== "") {
        //           this.minions.push(new TransformInstance(ci));
        //         } else if (ci.instance_type && ci.instance_type === "Summon") {
        //           const si = new SummonInstance(ci);
        //           if (si.use_your_pb) {
        //             si.your_pb = this.proficiency_modifier;
        //           }
        //           this.minions.push(si);
        //         } else if (ci.instance_type === "ComplexCreatureInstance") {
        //           console.log(ci);
        //           // debugger;
        //           const cci = new ComplexCreatureInstance(ci);
        //           cci.owner_char = this;
        //           if (cci.use_your_pb) {
        //             cci.your_pb = this.proficiency_modifier;
        //           }
        //           this.minions.push(cci);
        //         } else {
        //           this.minions.push(new BasicCreatureInstance(ci));
        //         }
        //       }
        //     }
        //     this.transform = piece && piece.transform && piece.transform.tool_proficiencies === undefined ? new TransformInstance(piece.transform) : null;
        //     this.use_transform = piece && piece.transform && piece.use_transform ? piece.use_transform : false;
        //   break;
        //   case "hp": 
        //     if (piece.resource_holder) {
        //       this.resource_holder = new CharacterResourceHolder(piece.resource_holder);
        //     } else {
        //       this.resource_holder_is_good = false;
        //       this.resource_holder = new CharacterResourceHolder();
        //       this.resource_holder.hp = piece.hp ? new CreatureHP(piece.hp) : new CreatureHP();
        //       this.resource_holder.hit_dice = [];
        //       if (piece && piece.hit_dice && piece.hit_dice.length > 0) {
        //         for (let i = 0; i < piece.hit_dice.length; i++) {
        //           const char_hit_dice = new HitDiceWithUsed(piece.hit_dice[i]);
        //           this.resource_holder.hit_dice.push(char_hit_dice);
        //         }
        //       }
        //       // Resources and slots are in spells.  
        //       // They're getting moved to hp as part of this.
        //     }
        //   break;
        //   case "spells": 
        //     // char_id: this._id,
        //     // console.log(piece);
        //     let get_special_resources = false;
        //     if (piece && piece.resources && !this.resource_holder_is_good) {
        //       // Resources and slots are now part of resource_holder, which is done in "hp".  
        //       // When loading all, "hp" is done before "spells", so this will get it in that first time before it's saved in the new format.
        //       this.resource_holder.resources = [];
        //       if (piece && piece.resources && piece.resources.length > 0) {
        //         for (let i = 0; i < piece.resources.length; i++) {
        //           const char_resource = new CharacterResourceWithUsed(piece.resources[i]);
        //           this.resource_holder.resources.push(char_resource);
        //         }
        //       }
        //       this.resource_holder.slots = [];
        //       if (piece && piece.slots && piece.slots.length > 0) {
        //         for (let i = 0; i < piece.slots.length; i++) {
        //           const char_slot = new CharacterSlot(piece.slots[i]);
        //           this.resource_holder.slots.push(char_slot);
        //         }
        //       }
        //       get_special_resources = true;
        //     }
        //     this.spells = [];
        //     if (piece && piece.spells && piece.spells.length > 0) {
        //       // console.log(piece.spells);
        //       for (let i = 0; i < piece.spells.length; i++) {
        //         const char_spell = new CharacterSpell(piece.spells[i]);
        //         this.spells.push(char_spell);
        //       }
        //     }
        //     this.abilities = [];
        //     if (piece && piece.abilities && piece.abilities.length > 0) {
        //       for (let i = 0; i < piece.abilities.length; i++) {
        //         const ability_piece = piece.abilities[i];
        //         if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
        //           this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
        //         }
        //         const char_ability = new CharacterAbility(ability_piece);
        //         this.abilities.push(char_ability);
        //       }
        //     }
        //     this.spell_as_abilities = [];
        //     if (piece && piece.spell_as_abilities && piece.spell_as_abilities.length > 0) {
        //       for (let i = 0; i < piece.spell_as_abilities.length; i++) {
        //         const ability_piece = piece.spell_as_abilities[i];
        //         if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
        //           this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
        //         }
        //         const char_ability = new CharacterAbility(ability_piece);
        //         this.spell_as_abilities.push(char_ability);
        //       }
        //     }
        //     this.extra_effects = [];
        //     if (piece && piece.extra_effects && piece.extra_effects.length > 0) {
        //       for (let i = 0; i < piece.extra_effects.length; i++) {
        //         const ability_piece = piece.extra_effects[i];
        //         if (get_special_resources && ability_piece && ability_piece.special_resource_used && ability_piece.special_resource_used > 0) {
        //           this.resource_holder.special_resources.push({ ability_true_id: ability_piece.true_id, used: ability_piece.special_resource_used });
        //         }
        //         const char_ee = new CharacterExtraEffects(ability_piece);
        //         this.extra_effects.push(char_ee);
        //       }
        //     }
        //   break;
        //   case "items": 
        //     this.items = [];
        //     // debugger;
        //     // console.log(piece.items);
        //     if (piece && piece.items && piece.items.length > 0) {
        //       const types: string[] = [];
        //       for (let i = 0; i < piece.items.length; i++) {
        //         const item = piece.items[i];
        //         if (!types.includes(item.item_type)) {
        //           types.push(item.item_type);
        //         }
        //         // if (item.item_type === "Gem") {
        //         //   console.log(item);
        //         // }
        //         let char_item: CharacterItem | null = null;
        //         switch (item.item_type) {
        //           case "Armor":
        //             char_item = new CharacterArmor(item);
        //           break;
        //           case "Weapon":
        //             char_item = new CharacterWeapon(item);
        //           break;
        //           case "Ammunition":
        //             char_item = new CharacterAmmunition(item);
        //           break;
        //           case "Bag":
        //             char_item = new CharacterContainer(item);
        //             this.containers.push(char_item as CharacterContainer);
        //           break;
        //           case "Container":
        //             char_item = new CharacterContainer(item);
        //             this.containers.push(char_item as CharacterContainer);
        //           break;
        //           case "Deck":
        //             char_item = new CharacterDeck(item);
        //           break;
        //           case "Gem":
        //             char_item = new CharacterGem(item);
        //           break;
        //           case "Book":
        //             char_item = new CharacterBook(item);
        //           break;
        //           case "Custom":
        //             if (item.true_id === "Equipment") {
        //               if (this.containers.filter(o => o.true_id === "Equipment").length === 0) {
        //                 char_item = new CharacterContainer(item);
        //                 this.containers.push(char_item as CharacterContainer);
        //               }
        //             } else if (item.true_id === "Storage") {
        //               if (this.containers.filter(o => o.true_id === "Storage").length === 0) {
        //                 char_item = new CharacterContainer(item);
        //                 this.containers.push(char_item as CharacterContainer);
        //               }
        //             } else {
        //               char_item = new CharacterCustomItem(item);
        //             }
        //           break;
        //           default:
        //             char_item = new CharacterBasicItem(item);
        //           break;
        //         }
        //         if (char_item) {
        //           if (char_item.source_id === "") {
        //             return "item";
        //           }
        //           this.items.push(char_item);
        //         }
        //       }
        //       if (this.containers.filter(o => o.name === "Equipment").length === 0) {
        //         const equipment = new CharacterContainer();
        //         equipment.name = "Equipment";
        //         equipment.true_id = "Equipment";
        //         this.containers.push(equipment);
        //         this.items.push(equipment);
        //         const storage = new CharacterContainer();
        //         storage.name = "Storage";
        //         storage.true_id = "Storage";
        //         this.containers.push(storage);
        //         this.items.push(storage);
        //       }
        //     }
        //     this.equipped_items = [];
        //     if (piece && piece.equipped_items && piece.equipped_items.length > 0) {
        //       for (let i = 0; i < piece.equipped_items.length; i++) {
        //         // All equipped items are also in items, and should have the same ids
        //         // All that gets saved to the db is the id
        //         if (this.equipped_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
        //           const obj_finder = this.items.filter(o => o.true_id === piece.equipped_items[i]);
        //           if (obj_finder.length === 1) {
        //             this.equipped_items.push(obj_finder[0]);
        //           }
        //         }
        //       }
        //     }
        //     this.attuned_items = [];
        //     if (piece && piece.attuned_items && piece.attuned_items.length > 0) {
        //       for (let i = 0; i < piece.attuned_items.length; i++) {
        //         // All equipped items are also in items, and should have the same ids
        //         // All that gets saved to the db is the id
        //         if (this.attuned_items.filter(o => o.true_id === piece.equipped_items[i]).length === 0) {
        //           const obj_finder = this.items.filter(o => o.true_id === piece.attuned_items[i]);
        //           if (obj_finder.length === 1) {
        //             this.attuned_items.push(obj_finder[0]);
        //           }
        //         }
        //       }
        //     }
        //     this.max_attuned_items = piece ? piece.max_attuned_items : 3;
        //     // console.log(piece);
        //     this.money = piece && piece.money ? new Money(piece.money) : new Money();
        //     this.other_possessions = piece ? piece.other_possessions : "";
        //     this.equipment_container.content_weight = -1;
        //     this.storage_container.content_weight = -1;
        //     const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
        //     this.items = this.items.filter(o => 
        //       container_ids.includes(o.true_id) || 
        //       container_ids.includes(o.in_char_container_id));
        //     this.recipes_known = piece && piece.recipes_known ? piece.recipes_known.map((o: any) => new CharacterRecipe(o)) : [];
        //     this.recipe_components = piece && piece.recipe_components ? piece.recipe_components.map((o: any) => new CharacterRecipeComponent(o)) : [];
        //   break;
        //   case "race": 
        //     console.log("Race disappearing sometimes. Aasimar, Uriel.");
        //     console.log(piece);
        //     this.race = piece && piece.race ? new CharacterRace(piece.race) : new CharacterRace();
        //     if (this.race.source_id === "") {
        //       return "race";
        //     }
        //     this.standard_features = piece && piece.standard_features ? new CharacterStandardFeatures(piece.standard_features) : new CharacterStandardFeatures();
        //     if (this.standard_features.source_id === "") {
        //       return "standard_features";
        //     }
        //   break;
        //   case "gifts": 
        //     // debugger;
        //     this.gifts = [];
        //     if (piece && piece.gifts && piece.gifts.length > 0) {
        //       for (let i = 0; i < piece.gifts.length; i++) {
        //         const cg = new CharacterGift(piece.gifts[i]);
        //         if (cg.source_id === "") {
        //           return "gift";
        //         }
        //         this.gifts.push(cg);
        //       }
        //     }
        //   break;
        //   case "bastion_facilities": 
        //     // debugger;
        //     this.bastion_facilities = [];
        //     if (piece && piece.bastion_facilities && piece.bastion_facilities.length > 0) {
        //       for (let i = 0; i < piece.bastion_facilities.length; i++) {
        //         const cbf = new CharacterBastionFacility(piece.bastion_facilities[i]);
        //         if (cbf.source_id === "") {
        //           return "bastion_facility";
        //         }
        //         this.bastion_facilities.push(cbf);
        //       }
        //     }
        //   break;
        //   case "classes": 
        //     this.classes = [];
        //     if (piece && piece.classes && piece.classes.length > 0) {
        //       // console.log(piece.classes[0]);
        //       const char_classes: CharacterClass[] = [];
        //       for (let i = 0; i < piece.classes.length; i++) {
        //         const cc = new CharacterClass(piece.classes[i]);
        //         if (cc.source_id === "") {
        //           return "game_class";
        //         }
        //         char_classes.push(cc);
        //       }
        //       this.classes = char_classes.sort((a, b) => (a.position > b.position ? 1 : -1));
        //     }
        //   break;
        //   // case "orgs": 
        //   //   this.orgs = [];
        //   //   if (piece && piece.orgs && piece.orgs.length > 0) {
        //   //     for (let i = 0; i < piece.orgs.length; i++) {
        //   //       this.orgs.push(new CharacterOrg(piece.orgs[i]));
        //   //     }
        //   //   }
        //   // break;
        //   case "background": 
        //     this.background = piece && piece.background ? new CharacterBackground(piece.background) : new CharacterBackground();
        //     if (this.background.source_id === "") {
        //       return "background";
        //     }
        //   break;
        // }
      }
    } else if (this.piece_version === 2) {
      switch (piece_name) {
        case "abcs": 
          this.spirit.ability_score_gen_method = piece.ability_score_gen_method;
          this.spirit.base_ability_scores = new AbilityScores(piece.base_ability_scores);
          this.spirit.race = new CharacterRace(piece.race);
          console.log(this.spirit.race);
          console.log(piece.race);
          this.spirit.background = new CharacterBackground(piece.background);
          this.spirit.classes = piece.classes ? piece.classes.map((o: any) => new CharacterClass(o)) : [];
          this.spirit.standard_features = new CharacterStandardFeatures(piece.standard_features);
          this.spirit.extra_feats = piece.extra_feats.map((o: any) => new CharacterFeat(o));
          this.spirit.gifts = piece.gifts.map((o: any) => new CharacterGift(o));
          for (const o of piece.spells) {
            const char_spell = new CharacterSpell(o);
            // console.log(char_spell);
            const cfs = this.spirit.char_feature_sources.find(o => o.true_id === char_spell.source_true_id || o.source_id === char_spell.source_id);
            if (cfs) {
              cfs.spellcasting.spells.push(char_spell);
            }
          }
          // this.mind.spells = piece.spells.map((o: any) => new CharacterSpell(o));
          // this.mind.abilities = piece.abilities.map((o: any) => new CharacterAbility(o));
          // this.mind.spell_as_abilities = piece.spell_as_abilities.map((o: any) => new CharacterAbility(o));
          // this.mind.extra_effects = piece.extra_effects.map((o: any) => new CharacterExtraEffects(o));
          this.spirit.optional_feature_base_ids = piece.optional_feature_base_ids;
        break;
        case "inventory": 
          this.inventory.items = [];
          // debugger;
          // console.log(piece.items);
          if (piece && piece.items && piece.items.length > 0) {
            const types: string[] = [];
            for (let i = 0; i < piece.items.length; i++) {
              const item = piece.items[i];
              if (!types.includes(item.item_type)) {
                types.push(item.item_type);
              }
              // if (item.item_type === "Gem") {
              //   console.log(item);
              // }
              let char_item: CharacterItem | null = null;
              switch (item.item_type) {
                case "Armor":
                  char_item = new CharacterArmor(item);
                break;
                case "Weapon":
                  char_item = new CharacterWeapon(item);
                break;
                case "Ammunition":
                  char_item = new CharacterAmmunition(item);
                break;
                case "Bag":
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                break;
                case "Container":
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                break;
                case "Deck":
                  char_item = new CharacterDeck(item);
                break;
                case "Gem":
                  char_item = new CharacterGem(item);
                break;
                case "Book":
                  char_item = new CharacterBook(item);
                break;
                case "Custom":
                  if (item.true_id === "Equipment") {
                    if (this.containers.filter(o => o.true_id === "Equipment").length === 0) {
                      char_item = new CharacterContainer(item);
                      this.containers.push(char_item as CharacterContainer);
                    }
                  } else if (item.true_id === "Storage") {
                    if (this.containers.filter(o => o.true_id === "Storage").length === 0) {
                      char_item = new CharacterContainer(item);
                      this.containers.push(char_item as CharacterContainer);
                    }
                  } else {
                    char_item = new CharacterCustomItem(item);
                  }
                break;
                default:
                  char_item = new CharacterBasicItem(item);
                break;
              }
              if (char_item) {
                if (char_item.source_id === "") {
                  return "item";
                }
                this.items.push(char_item);
              }
            }
            if (this.containers.filter(o => o.name === "Equipment").length === 0) {
              const equipment = new CharacterContainer();
              equipment.name = "Equipment";
              equipment.true_id = "Equipment";
              this.containers.push(equipment);
              this.items.push(equipment);
              const storage = new CharacterContainer();
              storage.name = "Storage";
              storage.true_id = "Storage";
              this.containers.push(storage);
              this.items.push(storage);
            }
          }
          this.inventory.money = piece && piece.money ? new Money(piece.money) : new Money();
          this.inventory.other_possessions = piece ? piece.other_possessions : "";
          this.inventory.equipment_container.content_weight = -1;
          this.inventory.storage_container.content_weight = -1;
          const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
          this.inventory.items = this.items.filter(o => 
            container_ids.includes(o.true_id) || 
            container_ids.includes(o.in_char_container_id));
          // this.recipes_known = piece && piece.recipes_known ? piece.recipes_known.map((o: any) => new CharacterRecipe(o)) : [];
          // this.recipe_components = piece && piece.recipe_components ? piece.recipe_components.map((o: any) => new CharacterRecipeComponent(o)) : [];
        break;
        case "misc": 
          // this.extra_damage_multipliers = [];
          // if (piece && piece.extra_damage_multipliers) {
          //   for (let i = 0; i < piece.extra_damage_multipliers.length; i++) {
          //     const dm = new DamageMultiplierSimple(piece.extra_damage_multipliers[i]);
          //     this.extra_damage_multipliers.push(dm);
          //   }
          // }
          // this.spirit.favorite_conditions = piece ? piece.favorite_conditions : [];
          this.spirit.conditions = [];
          if (piece && piece.conditions) {
            for (let i = 0; i < piece.conditions.length; i++) {
              const cc = new CharacterCondition(piece.conditions[i]);
              if (cc.source_id === "") {
                return "condition";
              }
              this.spirit.conditions.push(cc);
            }
          } else if (piece && piece.condition_hash) {
            const cond_ids = Object.keys(piece.condition_hash);
            for (const cond_id of cond_ids) {
              if (typeof piece.condition_hash[cond_id] === "object") {
                // this.condition_hash[cond_id] = piece.condition_hash[cond_id];
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id].level;
                cc.condition_source_true_id = piece.condition_hash[cond_id].source_true_id;
                this.spirit.conditions.push(cc);
              } else if (typeof piece.condition_hash[cond_id] === "number") {
                // this.condition_hash[cond_id] = { level: piece.condition_hash[cond_id], source_true_id: "" };
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id];
                this.spirit.conditions.push(cc);
              }
            }
          }
          this.spirit.condition_immunities = piece ? piece.condition_immunities : [];
          this.body.advantage = piece ? +piece.advantage : 0;
          this.body.concentrating_on = null;
          if (piece && piece.concentrating_on) {
            if (piece.concentrating_on.ability_type) {
              this.body.concentrating_on = new CharacterAbility(piece.concentrating_on);
              const source_id = this.body.concentrating_on.source_id;
              if (this.body.concentrating_on.source_type === "Class") {
                const class_finder = this.classes.filter(o => o.source_id === source_id);
                if (class_finder.length === 1) {
                  this.body.concentrating_on.connectSource(class_finder[0]);
                }
              }
            } else if (piece.concentrating_on.spell_id) {
              this.body.concentrating_on = new CharacterSpell(piece.concentrating_on);
            }
          }
          this.spirit.minions = [];
          if (piece && piece.minions && piece.minions.length > 0) {
            // console.log(piece.minions);
            for (const ci of piece.minions) {
              // debugger;
              this.spirit.minions.push(new TrueCreatureInstance(ci));
            }
          }
          // this.spirit.transform = piece && piece.transform && piece.transform.tool_proficiencies === undefined ? new TransformInstance(piece.transform) : null;
          this.use_transform = piece && piece.transform && piece.use_transform ? piece.use_transform : false;
          this.body.inspiration = piece.inspiration;
          // this.body.default_view = piece.default_view;
          this.spirit.base_size = piece.base_size;
          // this.size_modifier = piece.size_modifier;
          // this.overriding_size = piece.overriding_size;
          // this.speed_customizations = piece.speed_customizations;
          // this.sense_customizations = piece.sense_customizations;
          // this.saving_throw_customizations = piece.saving_throw_customizations;
          // this.skill_customizations = piece.skill_customizations;
          // this.max_ability_score_customizations = piece.max_ability_score_customizations;
          // this.ability_score_customizations = piece.ability_score_customizations;
          // this.display_speed = piece.display_speed;
          // this.AC_customization = piece.AC_customization;
          // this.non_skill_prof_customizations = piece.non_skill_prof_customizations;
          // this.weapon_mastery_customizations = piece.weapon_mastery_customizations.map((o: any) => new CharacterWeaponMastery(o));
          this.last_campaign_update = piece.last_campaign_update;
          // this.unarmed_strike_customizations = piece.unarmed_strike_customizations;
          // this.attunement_customization = piece.attunement_customization ? new UpgradableNumber(piece.attunement_customization) : null;
          this.character_options = new CharacterOptions(piece.character_options);
          this.spirit.knowledge = piece.knowledge;
        break;
        case "resources":
          this.body.resource_holder = new CharacterResourceHolder(piece.resource_holder);
        break;
        case "bastion_facilities": 
          this.spirit.bastion_facilities = piece.bastion_facilities.map((o: any) => new CharacterBastionFacility(o));
        break;
        case "notes": 
          this.notes.organizations = piece.organizations;
          this.notes.allies = piece.allies;
          this.notes.enemies = piece.enemies;
          this.notes.backstory = piece.backstory;
          this.notes.other_notes = piece.other_notes;
          this.notes.characteristics = piece.characteristics;
          this.notes.appearance = piece.appearance;
          this.notes.notes = piece.notes;
        break;
      }
    } else if (this.piece_version === 3) {
      switch (piece_name) {
        case "spirit": 
          this.spirit = new CharacterSpirit(piece);
        break;
        case "body":
          this.body = new CharacterBody(piece);
        break;
        case "inventory":
          this.inventory = new CharacterInventory(piece);
        break;
        case "notes":
          this.notes = new CharacterNotes(piece);
        break;
        case "options":
          this.character_options = new CharacterOptions(piece);
        break;
        case "customizations":
          this.customizations = new CharacterCustomizations(piece);
        break;
        default:
          this.spirit.apply_piece(piece_name, piece); 
        break;
      }
    }
    return "";
  }

  toDBObj(): any {
    const super_obj = super.toDBObj();
    return {
      ...super_obj,
      piece_version: 3,
      shared_id: this.shared_id,
      last_saved: this.last_saved,
      cookie_saved: this.cookie_saved,
      saved_race_and_class_string: this.race_and_class_string,
      saved_race_string: this.race_string,
      saved_class_string: this.class_string,
      image_url: this.image_url,
      bg_image_url: this.bg_image_url,
      last_campaign_update: this.last_campaign_update,
      old_campaign_id: this.old_campaign_id,
      saved_ac: this.armor_class,
    };
  }

  toCharacterDBObj = (piece: string) => {
    let obj: any = {};
    if (false) {
      // // Old version
      // switch (piece) {
      //   case "abilities": 
      //     obj = {
      //       char_id: this._id,
      //       ability_score_gen_method: this.ability_score_gen_method,
      //       base_ability_scores: this.base_ability_scores.toDBObj(),
      //     };
      //   break;
      //   case "features": 
      //     const concentrating_on = this.concentrating_on ? this.concentrating_on.toDBObj() : null;
      //     if (concentrating_on) {
      //       concentrating_on._name = this.concentrating_on?.name??"";
      //     }
      //     obj = {
      //       char_id: this._id,
      //       extra_feats: this.extra_feats.map(o => o.toDBObj()),
      //       extra_damage_multipliers: this.extra_damage_multipliers.map(o => o.toDBObj()),
      //       favorite_conditions: this.favorite_conditions,
      //       conditions: this.conditions.map(o => o.toDBObj()),
      //       condition_immunities: this.condition_immunities,
      //       advantage: this.advantage,
      //       concentrating_on,
      //       minions: this.minions.map(o => o.toDBObj()),
      //       transform: this.transform ? this.transform.toDBObj() : null,
      //       use_transform: this.use_transform,
      //       // adaptations: this.adaptations.map(o => o.toDBObj()),
      //     };
      //   break;
      //   case "hp": // It being called "hp" is because I used to have it only have hp.  It should be called "resources" now, but I'm going to leave it.
      //     obj = {
      //       char_id: this._id,
      //       resource_holder: this.resource_holder.toDBObj(),
      //     };
      //   break;
      //   case "spells":
      //     obj = {
      //       char_id: this._id,
      //       spells: this.spells.map(o => o.toDBObj()),
      //       abilities: this.abilities.map(o => o.toDBObj()),
      //       spell_as_abilities: this.spell_as_abilities.map(o => o.toDBObj()),
      //       extra_effects: this.extra_effects.map(o => o.toDBObj())
      //     };
      //   break;
      //   case "items": 
      //     obj = {
      //       char_id: this._id,
      //       items: this.items.map(o => o.toDBObj()),
      //       equipped_items: this.equipped_items.map(o => o.true_id),
      //       attuned_items: this.attuned_items.map(o => o.true_id),
      //       max_attuned_items: this.max_attuned_items,
      //       other_possessions: this.other_possessions,
      //       money: this.money.toDBObj(),
      //       recipes_known: this.recipes_known.map(o => o.toDBObj()),
      //       recipe_components: this.recipe_components.map(o => o.toDBObj()),
      //     };
      //   break;
      //   case "race": 
      //     if (this.standard_features.char_feature_bases.length === 0) {
      //       // I've sometimes seen the languages get unselected, but it was while I was making code changes, so it might be fine.
      //       console.log(this.standard_features);
      //       console.log(this.standard_features.toDBObj());
      //     }
      //     obj = {
      //       char_id: this._id,
      //       race: this.race.toDBObj(),
      //       standard_features: this.standard_features.toDBObj(),
      //     };
      //   break;
      //   case "gifts": 
      //     obj = {
      //       char_id: this._id,
      //       gifts: this.gifts.map(o => o.toDBObj())
      //     };
      //   break;
      //   case "bastion_facilities": 
      //     obj = {
      //       char_id: this._id,
      //       bastion_facilities: this.bastion_facilities.map(o => o.toDBObj())
      //     };
      //   break;
      //   case "classes": 
      //     obj = {
      //       char_id: this._id,
      //       classes: this.classes.map(o => o.toDBObj())
      //     };
      //   break;
      //   case "background": 
      //     obj = {
      //       char_id: this._id,
      //       background: this.background.toDBObj()
      //     };
      //   break;
      //   case "orgs": 
      //     const orgs: any[] = [];
      //     for (let i = 0; i < this.orgs.length; i++) {
      //       orgs.push(this.orgs[i].toDBObj());
      //     }
      //     obj = {
      //       char_id: this._id,
      //       orgs
      //     };
      //   break;
      // }
    } else {
      if (false) {
        // switch (piece) {
        //   case "abcs": 
        //     obj = {
        //       char_id: this._id,
        //       ability_score_gen_method: this.ability_score_gen_method,
        //       base_ability_scores: this.base_ability_scores.toDBObj(),
        //       race: this.race.toDBObj(), // Ancestry
        //       background: this.background.toDBObj(), // Background
        //       classes: this.classes.map(o => o.toDBObj()), // Class
        //       standard_features: this.standard_features.toDBObj(),
        //       extra_feats: this.extra_feats.map(o => o.toDBObj()),
        //       gifts: this.gifts.map(o => o.toDBObj()),
        //       spells: this.spells.map(o => o.toDBObj()),
        //       // These get created on repopulate, but we store customizations here.
        //       abilities: this.abilities.map(o => o.toDBObj()),
        //       spell_as_abilities: this.spell_as_abilities.map(o => o.toDBObj()),
        //       extra_effects: this.extra_effects.map(o => o.toDBObj()),
        //       optional_feature_base_ids: this.optional_feature_base_ids,
        //     };
        //   break;
        //   case "inventory": 
        //     obj = {
        //       char_id: this._id,
        //       items: this.items.map(o => o.toDBObj()),
        //       equipped_items: this.equipped_items.map(o => o.true_id),
        //       attuned_items: this.attuned_items.map(o => o.true_id),
        //       max_attuned_items: this.max_attuned_items,
        //       other_possessions: this.other_possessions,
        //       money: this.money.toDBObj(),
        //       recipes_known: this.recipes_known.map(o => o.toDBObj()),
        //       recipe_components: this.recipe_components.map(o => o.toDBObj()),
        //     };
        //   break;
        //   case "misc": 
        //     const concentrating_on = this.concentrating_on ? this.concentrating_on.toDBObj() : null;
        //     if (concentrating_on) {
        //       concentrating_on._name = this.concentrating_on?.name??"";
        //     }
        //     obj = {
        //       char_id: this._id,
        //       extra_damage_multipliers: this.extra_damage_multipliers.map(o => o.toDBObj()),
        //       favorite_conditions: this.favorite_conditions,
        //       conditions: this.conditions.map(o => o.toDBObj()),
        //       condition_immunities: this.condition_immunities,
        //       advantage: this.advantage,
        //       concentrating_on,
        //       minions: this.minions.map(o => o.toDBObj()),
        //       transform: this.transform ? this.transform.toDBObj() : null,
        //       use_transform: this.use_transform,
        //       inspiration: this.inspiration,
        //       default_view: this.default_view,
        //       base_size: this.base_size,
        //       size_modifier: this.size_modifier,
        //       overriding_size: this.overriding_size,
        //       speed_customizations: this.speed_customizations,
        //       sense_customizations: this.sense_customizations,
        //       saving_throw_customizations: this.saving_throw_customizations,
        //       skill_customizations: this.skill_customizations,
        //       max_ability_score_customizations: this.max_ability_score_customizations,
        //       ability_score_customizations: this.ability_score_customizations,
        //       display_speed: this.display_speed,
        //       AC_customization: this.AC_customization,
        //       non_skill_prof_customizations: this.non_skill_prof_customizations,
        //       weapon_mastery_customizations: this.weapon_mastery_customizations.map(o => o.toDBObj()),
        //       last_campaign_update: this.last_campaign_update,
        //       unarmed_strike_customizations: this.unarmed_strike_customizations,
        //       attunement_customization: this.attunement_customization?.toDBObj()??null,
        //       character_options: this.character_options.toDBObj(),
        //       knowledge: this.knowledge,
        //       // adaptations: this.adaptations.map(o => o.toDBObj()),
        //       // colors_for: this.colors_for,
        //     };
        //   break;
        //   case "resources":
        //     obj = {
        //       char_id: this._id,
        //       resource_holder: this.resource_holder.toDBObj(),
        //     };
        //   break;
        //   case "bastion_facilities": 
        //     obj = {
        //       char_id: this._id,
        //       bastion_facilities: this.bastion_facilities.map(o => o.toDBObj())
        //     };
        //   break;
        //   case "notes": 
        //     obj = {
        //       char_id: this._id,
        //       organizations: this.organizations,
        //       allies: this.allies,
        //       enemies: this.enemies,
        //       backstory: this.backstory,
        //       other_notes: this.other_notes,
        //       characteristics: this.characteristics,
        //       appearance: this.appearance,
        //       notes: this.notes,
        //     };
        //   break;
        // }
      } else {
        this.spirit.char_id = this._id;
        this.body.char_id = this._id;
        this.inventory.char_id = this._id;
        this.character_options.char_id = this._id;
        this.customizations.char_id = this._id;
        switch (piece) {
          case "spirit": obj = this.spirit.toDBObj(); break;
          case "body": obj = this.body.toDBObj(); break;
          case "inventory": obj = this.inventory.toDBObj(); break;
          case "options": 
            if (this.campaign && this.campaign.c_options) {
              this.character_options.options = this.campaign.c_options.clone();
            }
            obj = this.character_options.toDBObj(); 
          break;
          case "customizations": obj = this.customizations.toDBObj(); break;
          default: obj = this.spirit.toPieceDBObj(piece); break;
        }
      }
    }
    return obj;
  }

  clone(drop_ids = false): Character {
    const clone = new Character();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: Character, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.saved_ac = copyMe.saved_ac;
    
    this.piece_version = copyMe.piece_version;
    this.shared_id = copyMe.shared_id;
    this.last_saved = copyMe.last_saved;
    this.cookie_saved = copyMe.cookie_saved;
    this.last_campaign_update = copyMe.last_campaign_update;

    this.connected = copyMe.connected;

    this.spirit = copyMe.spirit.clone();
    this.mind = copyMe.mind;
    this.body = copyMe.body.clone();
    this.inventory = copyMe.inventory.clone();
    this.character_options = copyMe.character_options.clone();
    this.customizations = copyMe.customizations.clone();
    
    this.receivables = copyMe.receivables.map(o => o.clone());

    this.show_hidden = copyMe.show_hidden;
    this.show_extra_action_types = copyMe.show_extra_action_types;
    this.show_rituals = copyMe.show_rituals;
  }

  saved_race_and_class_string = "";
  get race_and_class_string(): string {
    const race_string = this.race_string;
    const class_string = this.class_string;
    if ([""].includes(race_string) && class_string === "No Class") {
      return this.saved_race_and_class_string;
    }
    return `${race_string} ${class_string}`;
  }

  saved_race_string = "";
  get race_string(): string {
    let str = "No Race";
    if (this.spirit.sidekick && this.spirit.sidekick_creature) {
      str = this.spirit.sidekick_creature.use_name;
    } else {
      str = this.spirit.race.use_name;
    }
    if (str === "No Race") {
      str = this.saved_race_string;
    }
    if (this.c_options.races_called === "Species" && str.includes("No Race")) {
      str = str.replace("No Race", "No Species");
    }
    return str;
  }

  saved_class_string: string = "";
  get class_string(): string {
    // if (this.name.includes("Marius")) {
    //   debugger;
    // }
    if (this.spirit.classes.length === 0) {
      return this.saved_class_string;
    } else if (this.spirit.classes.length === 1) {
      const char_class = this.spirit.classes[0];
      return `${ char_class.get_use_name() } ${ char_class.level }`;
    } else {
      return this.spirit.classes.sort((a, b) => (a.position > b.position ? 1 : -1)).map(char_class => {
        return `${ char_class.source?.name??"" } ${ char_class.level }`;
      }).join("/");
    }
  }

  get speed() { return this.mind.speed; }
  get inspiration() { return this.body.inspiration; }
  set inspiration(value: boolean) { this.body.inspiration = value; }
  get character_level() { return this.mind.character_level; }
  // get transform() { return this.spirit.transform; }
  get items() { return this.inventory.items; }
  get classes() { return this.spirit.classes; }
  get current_ability_scores() { return this.mind.current_ability_scores; }
  get conditions() { return this.spirit.conditions; }
  get alternate_condition_features() { return this.mind.alternate_condition_features; }
  get proficiency_modifier() { return this.mind.proficiency_modifier; }
  get hp() { return this.body.hp; }
  get asi_features() { return this.mind.asi_features; }
  get table_modifiers() { return this.mind.table_modifiers; }
  get race() { return this.spirit.race; }
  get gifts() { return this.spirit.gifts; }
  get slots() { return this.body.slots; }
  get formula_values() { return this.mind.formula_values; }
  get resource_holder() { return this.body.resource_holder; }
  get proficiencies() { return this.mind.proficiencies; }
  get proficiency_features() { return this.mind.proficiency_features; }
  get equipped_items() { return this.inventory.equipped_items; }
  get max_ac_dex_bonus_for_med_armor() { return this.mind.max_ac_dex_bonus_for_med_armor; }
  get size_option_features() { return this.mind.size_option_features; }
  get size_options() { return this.mind.size_options; }
  get base_size() { return this.spirit.base_size; }
  get initiative_modifier() { return this.mind.initiative_modifier; }
  get bonus_skill_profs_ids() { return this.spirit.bonus_skill_profs_ids; }
  get saving_throw_proficiencies() { return this.mind.saving_throw_proficiencies; }
  get summon_upgrade_features() { return this.mind.summon_upgrade_features; }
  get spell_as_abilities() { return this.mind.spell_as_abilities; }
  get armor_class() { return this.mind.armor_class === 0 ? this.saved_ac : this.mind.armor_class; }
  get max_ability_scores() { return this.mind.max_ability_scores; }
  get display_speed() { return this.mind.display_speed; }
  get extra_effects() { return this.mind.extra_effects; }
  get use_ac() { return this.mind.use_ac; }
  get advantages() { return this.mind.advantages; }
  get resources() { return this.body.resources; }
  get damage_multipliers() { return this.mind.damage_multipliers; }
  get minions() { return this.spirit.minions; }
  get transform() { return this.spirit.transform; }
  get active() { return this.c_char && this.c_char.active; }
  get actions() { return this.mind.actions; }
  get money() { return this.inventory.money; }
  get extra_attacks() { return this.mind.extra_attacks; }
  get full_action_action_types() { return this.mind.full_actions; }
  get bonus_action_action_types() { return this.mind.bonus_actions; }
  get reaction_action_types() { return this.mind.reactions; }
  get creature_type() { return this.customizations.creature_type ? this.customizations.creature_type : this.mind.creature_type; }
  get sidekick() { return this.spirit.sidekick; }
  get sidekick_creature_id() { return this.spirit.sidekick_creature_id; }
  get sidekick_creature() { return this.spirit.sidekick_creature; }
  get standard_weapon_mastery_weapons() { return this.mind.standard_weapon_mastery_weapons; }
  get weapon_masteries() { return this.mind.weapon_masteries; }
  get size_modifier() { return this.mind.size_modifier; }
  get senses() { return this.mind.senses; }
  get passive_bonuses() { return this.mind.passive_bonuses; }
  get advantage() { return this.body.advantage; }
  get fighting_styles() { return this.mind.fighting_styles; }
  get knowledge_features() { return this.mind.knowledge_features; }
  get learnable_features() { return this.mind.learnable_features; }
  get start_equipment_features() { return this.mind.start_equipment_features; }
  get shield_carried() { return this.inventory.shield_carried; }
  get armor_worn() { return this.inventory.armor_worn; }
  get containers() { return this.inventory.containers; }
  get encumbrance_level() { return this.mind.encumbrance_level; }
  get weight_carried() { return this.inventory.weight_carried; }
  get max_carrying_capacity() { return this.mind.max_carrying_capacity; }
  get other_possessions() { return this.inventory.other_possessions; }
  get attuned_items() { return this.inventory.attuned_items; }
  get max_attuned_items() { return this.mind.max_attuned_items; }
  get char_feature_bases() { return this.mind.char_feature_bases; }
  get expertises() { return this.mind.expertises; }
  get special_feat_accesses() { return this.mind.special_feat_accesses; }
  get hit_dice_string() { return this.body.hit_dice_string; }
  get c_options() {
    if (this.campaign && this.campaign.char_options) {
      return this.campaign.c_options;
    } else {
      return this.character_options.options;
    }
  }
}

export class CharacterSpirit implements Spirit {
  char_id = "";
  piece_version = 3;

  base_size: SizeType = 3;
  sidekick = false;
  sidekick_creature_id = "";
  sidekick_creature?: Creature;
  ability_score_gen_method: "Point Buy" | "Standard Array" | "Manual/Rolled" = "Point Buy";
  base_ability_scores = new AbilityScores();
  knowledge: CharacterKnowledge[] = [];

  standard_features = new CharacterStandardFeatures();
  race = new CharacterRace();
  background = new CharacterBackground();
  classes: CharacterClass[] = [];
  extra_feats: CharacterFeat[] = [];
  gifts: CharacterGift[] = [];
  bastion_facilities: CharacterBastionFacility[] = [];
  conditions: CharacterCondition[] = [];
  condition_immunities: string[] = [];
  optional_feature_base_ids: string[] = [];
  bonus_skill_profs_ids: string[] = [];

  get char_feature_sources(): CharacterFeatureSource[] {
    let char_feature_sources: CharacterFeatureSource[] = [];
    char_feature_sources = [this.race,this.background,...this.classes,...this.gifts,...this.conditions,...this.extra_feats,this.standard_features];
    let i = 0;
    while (i < char_feature_sources.length) {
      const cfs = char_feature_sources[i];
      char_feature_sources = [...char_feature_sources,...cfs.sub_char_feature_sources];
      ++i;
    }
    return char_feature_sources;
  }
  get feats() {
    const char_feature_sources = this.char_feature_sources;
    return char_feature_sources.filter(o => o instanceof CharacterFeat) as CharacterFeat[];
  }

  get character_level() {
    if (this.classes.length > 0) {
      return this.classes.reduce((accumulator: number, char_class: CharacterClass) => {
        return accumulator + char_class.level;
      }, 0);
    }
    return 0;
  }

  get race_ids(): string[] {
    return this.race.search_ids;
  }

  get subrace_ids(): string[] {
      return this.race.char_subrace?.search_ids??[];
  }

  get class_ids(): string[] {
    return this.classes.flatMap(o => o.search_ids);
  }

  get subclass_ids(): string[] {
    return this.classes.flatMap(o => o.char_subclass?.search_ids??[]);
  }

  get creature_type() {
    return this.race.creature_type;
  }

  minions: CreatureInstance[] = [];
  transform: CreatureInstance | null = null;
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.piece_version = obj.piece_version ? obj.piece_version : 3;
    }
  }

  apply_piece(piece_name: string, piece: any) {
    if (this.piece_version === 3) {
      switch (piece_name) {
        case "abcs": 
          this.ability_score_gen_method = piece.ability_score_gen_method;
          this.base_ability_scores = new AbilityScores(piece.base_ability_scores);
          this.race = new CharacterRace(piece.race);
          this.background = new CharacterBackground(piece.background);
          this.classes = piece.classes.map((o: any) => new CharacterClass(o));
          this.standard_features = new CharacterStandardFeatures(piece.standard_features);
          this.extra_feats = piece.extra_feats.map((o: any) => new CharacterFeat(o));
          this.gifts = piece.gifts.map((o: any) => new CharacterGift(o));
        break;
        case "misc": 
          this.conditions = [];
          if (piece && piece.conditions) {
            for (let i = 0; i < piece.conditions.length; i++) {
              const cc = new CharacterCondition(piece.conditions[i]);
              if (cc.source_id === "") {
                return "condition";
              }
              this.conditions.push(cc);
            }
          } else if (piece && piece.condition_hash) {
            const cond_ids = Object.keys(piece.condition_hash);
            for (const cond_id of cond_ids) {
              if (typeof piece.condition_hash[cond_id] === "object") {
                // this.condition_hash[cond_id] = piece.condition_hash[cond_id];
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id].level;
                cc.condition_source_true_id = piece.condition_hash[cond_id].source_true_id;
                this.conditions.push(cc);
              } else if (typeof piece.condition_hash[cond_id] === "number") {
                // this.condition_hash[cond_id] = { level: piece.condition_hash[cond_id], source_true_id: "" };
                const cc = new CharacterCondition();
                cc.source_id = cond_id;
                cc.level = piece.condition_hash[cond_id];
                this.conditions.push(cc);
              }
            }
          }
          this.condition_immunities = piece ? piece.condition_immunities : [];
          this.optional_feature_base_ids = piece ? piece.optional_feature_base_ids : [];
          this.minions = [];
          if (piece && piece.minions && piece.minions.length > 0) {
            // console.log(piece.minions);
            for (const ci of piece.minions) {
              // debugger;
              this.minions.push(new TrueCreatureInstance(ci));
            }
          }
          this.transform = piece && piece.transform ? new TrueCreatureInstance(piece.transform) : null;
          
          this.base_size = piece.base_size;
          this.knowledge = piece.knowledge;
        break;
        case "bastion_facilities": 
          this.bastion_facilities = piece.bastion_facilities.map((o: any) => new CharacterBastionFacility(o));
        break;
      }
    }
    return "";
  }

  toDBObj() {
    return {
      char_id: this.char_id,
      piece_version: this.piece_version,
    };
  }

  toPieceDBObj(piece: string) {
    let obj: any = {};
    switch (piece) {
      case "abcs": 
        obj = {
          char_id: this.char_id,
          ability_score_gen_method: this.ability_score_gen_method,
          base_ability_scores: this.base_ability_scores.toDBObj(),
          race: this.race.toDBObj(), // Ancestry
          background: this.background.toDBObj(), // Background
          classes: this.classes.map(o => o.toDBObj()), // Class
          standard_features: this.standard_features.toDBObj(),
          extra_feats: this.extra_feats.map(o => o.toDBObj()),
          gifts: this.gifts.map(o => o.toDBObj()),
        };
      break;
      case "misc": 
        obj = {
          char_id: this.char_id,
          conditions: this.conditions.map(o => o.toDBObj()),
          condition_immunities: this.condition_immunities,
          optional_feature_base_ids: this.optional_feature_base_ids,
          minions: this.minions.map(o => o.toDBObj()),
          transform: this.transform ? this.transform.toDBObj() : null,
          base_size: this.base_size,
          knowledge: this.knowledge,
        };
      break;
      case "bastion_facilities": 
        obj = {
          char_id: this.char_id,
          bastion_facilities: this.bastion_facilities.map(o => o.toDBObj())
        };
      break;
    }
    return obj;
  }

  clone(drop_ids = false): CharacterSpirit {
    const clone = new CharacterSpirit();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterSpirit, drop_ids = false): void {
    this.char_id = copyMe.char_id;
    this.piece_version = copyMe.piece_version;
    this.sidekick = copyMe.sidekick;
    this.sidekick_creature_id = copyMe.sidekick_creature_id;
    this.sidekick_creature = copyMe.sidekick_creature;
    this.knowledge = copyMe.knowledge.map(o => { return {...o}; });
    this.base_size = copyMe.base_size;

    this.ability_score_gen_method = copyMe.ability_score_gen_method;
    this.base_ability_scores = copyMe.base_ability_scores.clone();

    this.race = copyMe.race.clone();
    this.gifts = copyMe.gifts.map(o => o.clone());
    this.bastion_facilities = copyMe.bastion_facilities.map(o => o.clone());
    this.classes = copyMe.classes.map(o => o.clone());
    this.background = copyMe.background.clone();
    this.standard_features = copyMe.standard_features.clone();
    this.extra_feats = copyMe.extra_feats.map(o => o.clone());
    this.conditions = copyMe.conditions.map(o => o.clone());
    this.condition_immunities = [...copyMe.condition_immunities];
    this.optional_feature_base_ids = [...copyMe.optional_feature_base_ids];

    this.minions = copyMe.minions.map(o => o.clone());
    this.transform = copyMe.transform?.clone()??null;
  }
}

export class CharacterMind implements Mind {
  // Collections of CFSs
  char_feature_sources: CharacterFeatureSource[] = []; // All CFSs, compiled from the ones in Spirit and Inventory
  // These are all usually subs, but sometimes we need to quickly find them, so this provides a way to do so.
  special_features: CharacterSpecialFeature[] = [];
  eldritch_invocations: CharacterEldritchInvocation[] = [];
  fighting_styles: CharacterFightingStyle[] = [];
  feats: CharacterFeat[] = [];
  pact_boon?: CharacterPactBoon;

  table_entries: { table_type: string, table_id: string, entry: TableEntry }[] = [];
  tables_changed = false;
  tables_loaded = false;
  weapon_masteries_ready = false;
  // All CFBs
  char_feature_bases: CharacterFeatureBase[] = [];
  // All CFs
  features: CharacterFeature[] = [];

  // CFs by type group
  resource_features: CharacterFeature[] = [];
  ability_features: CharacterFeature[] = [];
  characteristic_features: CharacterFeature[] = [];
  asi_features: CharacterFeature[] = [];
  ability_score_features: { cf: CharacterFeature; casibf: CharacterASIBaseFeature; }[] = [];
  language_features: CharacterFeature[] = [];
  proficiency_features: CharacterFeature[] = [];
  modifier_features: CharacterFeature[] = [];
  spell_modifier_features: CharacterFeature[] = [];
  disable_action_features: CharacterFeature[] = [];
  attack_upgrades: CharacterFeature[] = [];
  weapon_damage_upgrades: CharacterFeature[] = [];
  extra_effects_features: CharacterFeature[] = [];
  extra_effects: CharacterExtraEffects[] = [];
  light_attack_add_damage_mods: CharacterFeature[] = [];
  source_features: CharacterFeature[] = [];
  sense_features: CharacterFeature[] = [];
  start_equipment_features: CharacterFeature[] = [];
  misc_features: CharacterFeature[] = [];
  spellcasting_features: CharacterFeature[] = [];
  alternate_condition_features: AlternateConditionFeature[] = [];
  ignore_stealth_disadvantage_for_armor_type_ids: string[] = [];
  transform_upgrades: CharacterFeature[] = [];
  weapon_mastery_features: CharacterFeature[] = [];
  cwms: CharacterWeaponMastery[] = [];
  cwmms: CharacterWeaponMasteryMaster[] = [];

  knowledge_features: CharacterFeature[] = [];
  learnable_features: CharacterFeature[] = [];
  spell_damage_options: CharacterFeature[] = [];
  knowledgeForKey(key: string) {
    const cf = this.knowledge_features.find(o => 
      o.floating_feature instanceof KnowledgeFeature && 
      o.floating_feature.usage_key === key);
    if (cf && cf.floating_feature instanceof KnowledgeFeature) {
      return cf.feature_options as CharacterKnowledge[];
    }
    return [];
  }

  base_size: SizeType = 3;
  size_modifier: number = 0;
  overriding_size: SizeType = 0;
  size_option_features: SizeOptions[] = [];
  size_options: SizeType[] = [];
  get use_size() {
    if (this.overriding_size === 0) {
      const use_size = this.base_size + this.size_modifier;
      if (SIZE_MAP[use_size]) {
        return use_size as SizeType;
      } else {
        return this.base_size;
      }
    } else {
      return this.overriding_size;
    }
  }
  size_bonus_for_carrying = 0;
  carrying_capacity_mult = 0;
  max_carrying_capacity = 0;

  base_ability_scores = new AbilityScores();
  max_ability_scores = new AbilityScores();
  bonus_ability_score_modifiers = new AbilityScores();
  override_ability_scores = new AbilityScores();
  current_ability_scores = new AbilityScores();

  max_attuned_items = 3;

  character_level = 0;
  proficiency_modifier = 0;

  override_creature_type?: CreatureType | undefined;
  languages_known: string[] = [];
  extra_languages: string[] = [];
  speed: { walk: number; swim: number; climb: number; fly: number; burrow: number; } = { walk: 30, swim: 0, climb: 0, fly: 0, burrow: 0 };
  display_speed: "walk" | "swim" | "climb" | "fly" | "burrow" = "walk";
  senses: CharacterSense[] = [];

  damage_multipliers: DamageMultiplierSimple[] = [];
  get damage_immunities() { return this.damage_multipliers.filter(o => o.multiplier === 0).map(o => o.damage_type) }
  extra_damage_immunities = "";
  get damage_resistances() { return this.damage_multipliers.filter(o => o.multiplier === 0.5).map(o => o.damage_type) }
  extra_damage_resistances = "";
  get damage_vulnerabilities() { return this.damage_multipliers.filter(o => o.multiplier === 2).map(o => o.damage_type) }
  extra_damage_vulnerabilities = "";

  getDTsForMultiplier(multiplier: number) {
    let dts = this.getDTsForMultiplierSimple(multiplier);
    if (multiplier === 0) {
      dts.push(...this.getUpgradedDTs());
    } else if (multiplier === 0.5) {
      const immune = this.getDTsForMultiplier(0);
      dts = dts.filter(dt => !immune.includes(dt));
    } 
    return common_util.unique_array(dts);
  }

  getDTsForMultiplierSimple(multiplier: number) {
    return common_util.unique_array(this.damage_multipliers.filter(o => o.multiplier === multiplier).map(o => o.damage_type));
  }

  getUpgradedDTs() {
    const upgraded: DamageType[] = [];
    const res_dms = this.damage_multipliers.filter(o => o.multiplier === 0.5 && o.multiplier_improvement);
    for (const dm of res_dms) {
      // Look for another dm for the same type which is also resistance.
      const other_dm = this.damage_multipliers.find(o => dm.damage_type === dm.damage_type && o.true_id !== dm.true_id && o.multiplier === 0.5);
      if (other_dm && dm.damage_type !== "" && !upgraded.includes(dm.damage_type)) {
        upgraded.push(dm.damage_type);
      }
    }
    return upgraded;
  }

  formula_values: { [key: string]: number; } = {};
  string_hash: { [key: string]: string; } = {};
  upgradable_hash: { [key: string]: UpgradableNumber; } = {};

  base_armor_class = 0;
  max_ac_dex_bonus_for_med_armor = 0;
  armor_class = 0;
  saved_armor_class = 10;
  
  get use_ac() {
    return this.armor_class;
  }

  initiative_modifier = 0;
  initiative_bonuses: Bonus[] = [];
  initiative_check = new CharacterRoll();
  get passive_initiative() {
    return 10 + this.initiative_modifier;
  }

  saving_throw_bonuses: Bonus[] = [];
  saving_throw_profs: { [ability_score: string]: number; } = {};
  saving_throws: { [ability_score: string]: CharacterRoll; } = {};
  jack_of_all_trades = false;
  proficiencies: CharacterProficiency[] = [];
  skill_proficiencies: CharacterProficiency[] = [];
  tool_proficiencies: CharacterProficiency[] = [];
  special_proficiencies: CharacterProficiency[] = [];
  weapon_proficiencies: CharacterProficiency[] = [];
  armor_proficiencies: CharacterProficiency[] = [];
  saving_throw_proficiencies: CharacterProficiency[] = [];
  expertises: CharacterProficiency[] = [];
  skill_ability_overrides: SkillAbilityOverrideFeature[] = [];
  ability_checks: { 
    STR: CharacterRoll; 
    DEX: CharacterRoll; 
    CON: CharacterRoll; 
    INT: CharacterRoll; 
    WIS: CharacterRoll; 
    CHA: CharacterRoll; 
  } = {
    STR: new CharacterRoll(),
    DEX: new CharacterRoll(),
    CON: new CharacterRoll(),
    INT: new CharacterRoll(),
    WIS: new CharacterRoll(),
    CHA: new CharacterRoll()
  };
  skill_checks: { skill_id: string; skill_check: CharacterRoll; }[] = [];
  passive_bonuses: { perception: number; insight: number; investigation: number; } = { perception: 0, insight: 0, investigation: 0 };
  advantages: CharacterAdvantage[] = [];
  greater_advantages: GreaterAdvantage[] = [];
  extra_attacks = 0;
  unarmed_strike_scores: string[] = [];
  unarmed_strike_bonus_action = false;
  unarmed_strike_damage_types: DamageType[] = [];
  unarmed_strike_size = 0;
  unarmed_strike_count = 0;
  unarmed_strike_add_attack_to_dmg = false;
  table_modifiers: Modifier[] = [];
  spell_modifiers: CharacterAbilityModifier[] = [];
  range_bonuses: RangeBonus[] = [];
  rerolls: Reroll[] = [];
  minimum_rolls: MinimumRoll[] = [];
  attack_bonuses: Bonus[] = [];
  damage_bonuses: Bonus[] = [];
  check_bonuses: Bonus[] = [];
  ritual_only: CharacterSpell[] = [];
  abilities: CharacterAbility[] = [];
  spells: CharacterSpell[] = [];
  spellbook_spells: CharacterSpell[] = [];
  spell_as_abilities: CharacterAbility[] = [];
  spell_slot_level_progress: { [type_id: string]: number } = {};

  actions: CharacterAction[] = [];
  full_actions: ActionType[] = [];
  bonus_actions: ActionType[] = [];
  reactions: ActionType[] = [];
  action_type_upgrades: ActionTypeUpgrade[] = [];
  traits: CharacterAction[] = [];
  legendary_actions: CharacterAction[] = [];
  lair_actions: CharacterAction[] = [];
  
  merged_special_feature_types: string[][] = [];

  spellcasting_ability = "";
  spell_dc = new UpgradableNumber();
  spell_attack = new UpgradableNumber();
  non_magic_attack = new UpgradableNumber();
  magic_dmg_bonus = new UpgradableNumber();
  non_magic_dmg_bonus = new UpgradableNumber();

  dragonmarked_source_id = "";
  wild_talent_source_id = "";
  creature_type: CreatureType = "Humanoid";
  subtypes: string[] = [];
  swarm = false;
  tags: string[] = [];
  age_category = "";

  // Partially Calculated

  // Calculated attributes
  conditions_from_features: CharacterConditionFeature[] = [];
  summon_upgrade_features: CharacterFeature[] = [];
  limit_spoof_features: CharacterFeature[] = [];
  rest_alteration_features: CharacterFeature[] = [];

  characteristic_feature_source_names: string[] = [];
  show_extra_conditions: string[] = [];

  encumbrance_level = 0;
  special_feat_accesses: string[] = [];

  recalc_time = -1;

  saved_race_and_class_string = "";
  saved_race_string = "";
  saved_race_ids: string[] = [];
  saved_subrace_ids: string[] = [];
  saved_class_info: { class_id: string, subclass_id: string, level: number, class_name: string }[] = [];
  // saved_subclass_ids: string[] = [];
  // saved_campaign_id: string = "";
  saved_extras_string = "";

  get weapon_masteries(): CharacterWeaponMastery[] {
    return this.cwms;
    // return [...this.cwms,...this.weapon_mastery_customizations,...this.weapon_mastery_overrides];
  }

  get standard_weapon_masteries() {
    return this.cwms.filter(o => o.weapon_limits.some(o2 => o2.isEmpty(["item_ids"])));
  }

  get standard_weapon_mastery_weapons() {
    const ids: string[] = [];
    for (const cwm of this.cwms) {
      const limit = cwm.weapon_limits.find(o => o.isEmpty(["item_ids"]));
      if (limit) {
        ids.push(...limit.item_ids);
      }
    }
    return ids;
  }
  
  get weapon_keyword_upgrades(): WeaponKeywordUpgradeFeature[] {
    return this.attack_upgrades.filter(o => o.floating_feature instanceof WeaponKeywordUpgradeFeature).map(o => o.floating_feature) as WeaponKeywordUpgradeFeature[];
  }

  get weapon_keyword_upgrade2s(): WeaponKeywordUpgradeFeature2[] {
    return this.attack_upgrades.filter(o => o.floating_feature instanceof WeaponKeywordUpgradeFeature2).map(o => o.floating_feature) as WeaponKeywordUpgradeFeature2[];
  }
  // For this one we don't need a constructor, db obj, or copy stuff.
  // It's all populated during repopulate.
}

export class CharacterBody implements Body {
  char_id = "";
  name = "";
  image_url = "";
  resource_holder = new CharacterResourceHolder();
  get hp() { return this.resource_holder.hp; }
  set hp(value: CreatureHP) { this.resource_holder.hp = value; }
  get hit_dice() { return this.resource_holder.hit_dice; }
  set hit_dice(value: HitDiceWithUsed[]) { this.resource_holder.hit_dice = value; }
  // These ones should be gets
  concentrating_on: CharacterSpell | StoredSpell | CharacterAbility | null = null;
  advantage = 0;
  inspiration = false;
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.name = obj.name ? obj.name : "";
      this.image_url = obj.image_url ? obj.image_url : "";
      this.resource_holder = obj.resource_holder ? new CharacterResourceHolder(obj.resource_holder) : new CharacterResourceHolder();
      this.hp = obj.hp ? new CreatureHP(obj.hp) : new CreatureHP();
      this.hit_dice = obj.hit_dice ? obj.hit_dice.map((o: any) => new HitDiceWithUsed(o)) : [];
      this.concentrating_on = null; // I'm going to have to change how this works. I think I want to make it store the object type, source_true_id, and true_id.
      // if (obj.concentrating_on) {
      //   if (obj.concentrating_on.ability_type) {
      //     this.concentrating_on = new CharacterAbility(obj.concentrating_on);
      //     const source_id = this.concentrating_on.source_id;
      //     if (this.concentrating_on.source_type === "Class") {
      //       const class_finder = this.classes.filter(o => o.source_id === source_id);
      //       if (class_finder.length === 1) {
      //         this.concentrating_on.connectSource(class_finder[0]);
      //       }
      //     }
      //   } else if (piece.concentrating_on.spell_id) {
      //     this.concentrating_on = new CharacterSpell(piece.concentrating_on);
      //   }
      // }
      this.advantage = obj.advantage ? obj.advantage : 0;
      this.inspiration = obj.inspiration ? obj.inspiration : false;
    }
  }

  get hit_dice_string() {
    let response = "";
    for (let i = 0; i < this.hit_dice.length; i++) {
      const hd = this.hit_dice[i];
      if (response !== "") {
        response += ", ";
      }
      response += `${hd.count}d${hd.size}`;
    }
    return response;
  }

  get resources() {
    return this.resource_holder.resources;
  }
  get slots() {
    return this.resource_holder.slots;
  }
  get max_slot_level() {
    return Math.max(...this.slots.map(o => o.level), 0);
  }
  
  get use_slots(): CharacterSlot[] {
    const resource_slots = this.resources.filter(o => o.is_slot).map(o => o.slot);
    return [...this.slots,...resource_slots];
  }
  removeSlots(type_id: string) {
    this.resource_holder.slots = this.resource_holder.slots.filter(o => !o.type_id.includes(type_id));
  }

  toDBObj() {
    return {
      char_id: this.char_id,
      name: this.name,
      image_url: this.image_url,
      resource_holder: this.resource_holder.toDBObj(),
      hp: this.hp.toDBObj(),
      hit_dice: this.hit_dice.map(o => o.toDBObj()),
      resources: this.resources.map(o => o.toDBObj()),
      advantage: this.advantage,
      inspiration: this.inspiration,
    };
  }

  clone() {
    const clone = new CharacterBody();
    clone.copy(this);
    return clone;
  }

  copy(copyMe: CharacterBody) {
    this.char_id = copyMe.char_id;
    this.name = copyMe.name;
    this.image_url = copyMe.image_url;
    this.resource_holder = copyMe.resource_holder.clone();
    this.hp = copyMe.hp.clone();
    this.hit_dice = copyMe.hit_dice.map(o => o.clone());
    this.concentrating_on = copyMe.concentrating_on;
    this.advantage = copyMe.advantage;
    this.inspiration = copyMe.inspiration;
  }
}

export class CharacterInventory {
  char_id = "";
  items: CharacterItem[] = [];
  money: Money = new Money();
  other_possessions = "";
  get equipped_items() {
    return this.items.filter(o => o.equipped);
  }
  get attuned_items() {
    return this.items.filter(o => o.attuned);
  }
  get attunable_items() {
    return this.items.filter(o => o.attunement);
  }
  get containers() {
    return this.items.filter(o => o instanceof CharacterContainer) as CharacterContainer[];
  }
  get char_feature_sources() {
    const equipped_items = this.equipped_items;
    return equipped_items.filter(o => !o.attunement || o.attuned);
  }

  get armor_worn(): boolean {
    const armor = this.equipped_items.find(o => o.base_item instanceof Armor && o.base_item.armor_type_name !== "Shield");
    if (armor) {
      return true;
    }
    return false;
  }
  get shield_carried(): boolean {
    const shield = this.equipped_items.find(o => o.base_item instanceof Armor && o.base_item.armor_type_name === "Shield");
    if (shield) {
      return true;
    }
    return false;
  }
  get ammunitions() {
    return this.items.filter(o => o instanceof CharacterAmmunition) as CharacterAmmunition[];
  }

  get equipment_container(): CharacterContainer {
    const equipment_finder = this.containers.filter(o => o.true_id === "Equipment");
    if (equipment_finder.length === 1) {
      return equipment_finder[0];
    } else {
      const equipment = new CharacterContainer();
      equipment.name = "Equipment";
      equipment.true_id = "Equipment";
      this.containers.push(equipment);
      this.items.push(equipment);
      return equipment;
    }
  }

  get storage_container(): CharacterContainer {
    const storage_finder = this.containers.filter(o => o.true_id === "Storage");
    if (storage_finder.length === 1) {
      return storage_finder[0];
    } else {
      const storage = new CharacterContainer();
      storage.name = "Storage";
      storage.true_id = "Storage";
      this.containers.push(storage);
      this.items.push(storage);
      return storage;
    }
  }

  get weight_carried(): number {
    let weight = 0;

    for (const container of this.containers.filter(o => 
      (o.true_id === "Equipment" || o.equipped) && 
      o.true_id !== "Storage")) {
      weight += container.weight;
      if (!container.ignore_weight) {
        weight += container.content_weight;
      }
    }

    return weight;
  }
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.items = [];
      if (obj.items && obj.items.length > 0) {
        const types: string[] = [];
        for (let i = 0; i < obj.items.length; i++) {
          const item = obj.items[i];
          if (!types.includes(item.item_type)) {
            types.push(item.item_type);
          }
          let char_item: CharacterItem | null = null;
          switch (item.item_type) {
            case "Armor":
              char_item = new CharacterArmor(item);
            break;
            case "Weapon":
              char_item = new CharacterWeapon(item);
            break;
            case "Ammunition":
              char_item = new CharacterAmmunition(item);
            break;
            case "Bag":
              char_item = new CharacterContainer(item);
              this.containers.push(char_item as CharacterContainer);
            break;
            case "Container":
              char_item = new CharacterContainer(item);
              this.containers.push(char_item as CharacterContainer);
            break;
            case "Deck":
              char_item = new CharacterDeck(item);
            break;
            case "Gem":
              char_item = new CharacterGem(item);
            break;
            case "Book":
              char_item = new CharacterBook(item);
            break;
            case "Custom":
              if (item.true_id === "Equipment") {
                if (this.containers.filter(o => o.true_id === "Equipment").length === 0) {
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                }
              } else if (item.true_id === "Storage") {
                if (this.containers.filter(o => o.true_id === "Storage").length === 0) {
                  char_item = new CharacterContainer(item);
                  this.containers.push(char_item as CharacterContainer);
                }
              } else {
                char_item = new CharacterCustomItem(item);
              }
            break;
            default:
              char_item = new CharacterBasicItem(item);
            break;
          }
          if (char_item) {
            this.items.push(char_item);
          }
        }
        if (this.containers.filter(o => o.name === "Equipment").length === 0) {
          const equipment = new CharacterContainer();
          equipment.name = "Equipment";
          equipment.true_id = "Equipment";
          this.containers.push(equipment);
          this.items.push(equipment);
          const storage = new CharacterContainer();
          storage.name = "Storage";
          storage.true_id = "Storage";
          this.containers.push(storage);
          this.items.push(storage);
        }
      }
      const container_ids = this.items.filter(o => o.item_type === "Container").map(o => o.true_id);
      this.items = this.items.filter(o => 
        container_ids.includes(o.true_id) || 
        container_ids.includes(o.in_char_container_id));

      this.money = obj.money ? new Money(obj.money) : new Money();
      this.other_possessions = obj.other_possessions ? obj.other_possessions : "";
    }
  }

  toDBObj() {
    return {
      char_id: this.char_id,
      items: this.items.map(o => o.toDBObj()),
      money: this.money.toDBObj(),
      other_possessions: this.other_possessions,
    };
  }

  clone(drop_ids = false): CharacterInventory {
    const clone = new CharacterInventory();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterInventory, drop_ids = false): void {
    this.char_id = copyMe.char_id;
    this.items = copyMe.items.map(o => o.clone());
    this.money = copyMe.money.clone();
    this.other_possessions = copyMe.other_possessions;
  }
}

export class CharacterNotes {
  char_id = "";
  organizations = "";
  allies = "";
  enemies = "";
  backstory = "";
  other_notes = "";

  characteristics: { [key: string]: string } = {
    faith: "",
    alignment: "Neutral",
    lifestyle: "Comfortable (2 GP)",
    hair: "",
    skin: "",
    eyes: "",
    height: "",
    weight: "",
    age: "",
    gender: ""
  };
  appearance = "";
  notes = "";
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.organizations = obj.organizations ? obj.organizations : "";
      this.allies = obj.allies ? obj.allies : "";
      this.enemies = obj.enemies ? obj.enemies : "";
      this.backstory = obj.backstory ? obj.backstory : "";
      this.other_notes = obj.other_notes ? obj.other_notes : "";
      this.characteristics = obj.characteristics ? obj.characteristics : "";
      this.appearance = obj.appearance ? obj.appearance : "";
      this.notes = obj.notes ? obj.notes : "";
    }
  }

  toDBObj() {
    return {
      char_id: this.char_id,
      organizations: this.organizations,
      allies: this.allies,
      enemies: this.enemies,
      backstory: this.backstory,
      other_notes: this.other_notes,
      characteristics: this.characteristics,
      appearance: this.appearance,
      notes: this.notes
    };
  }

  clone(drop_ids = false): CharacterNotes {
    const clone = new CharacterNotes();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterNotes, drop_ids = false): void {
    this.char_id = copyMe.char_id;
    this.organizations = copyMe.organizations;
    this.allies = copyMe.allies;
    this.enemies = copyMe.enemies;
    this.backstory = copyMe.backstory;
    this.other_notes = copyMe.other_notes;
    this.characteristics = copyMe.characteristics;
    this.appearance = copyMe.appearance;
    this.notes = copyMe.notes;
  }
}

export class CharacterOptions {
  char_id = "";
  options = new COptions();
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.options = obj.options ? new COptions(obj.options) : new COptions();
    }
  }

  toDBObj(): any {
    return {
      char_id: this.char_id,
      options: this.options.toDBObj(),
    };
  }

  clone(drop_ids = false): CharacterOptions {
    const clone = new CharacterOptions();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterOptions, drop_ids = false): void {
    this.char_id = copyMe.char_id;
    this.options = copyMe.options.clone();
  }
}

export interface Customization {
  [ability_score: string]: { 
    [type: string]: { // Override, Modifier, Proficiency Override
      value: number | null, 
      notes: string 
    }
  },
}

export class CharacterRoll {
  modifier = 0;
  proficiency = 0;
  rolls: RollPlus[] = [];
  advantage = 0;
  advantages: CharacterAdvantage[] = [];

  constructor(obj?: any) {
    if (obj) {
      this.modifier = obj.modifier ? obj.modifier : 0;
      this.proficiency = obj.proficiency ? obj.proficiency : 0;
      this.rolls = obj.rolls ? obj.rolls.map((o: any) => new RollPlus(o)) : [];
      this.advantage = obj.advantage ? obj.advantage : 0;
      this.advantages = obj.advantages ? obj.advantages : [];
    }
  }

  toDBObj() {
    return {
      modifier: this.modifier,
      proficiency: this.proficiency,
      rolls: this.rolls.map(o => o.toDBObj()),
      advantage: this.advantage,
      advantages: this.advantages
    }
  }

  copy(copyMe: CharacterRoll) {
    this.modifier = copyMe.modifier;
    this.proficiency = copyMe.proficiency;
    this.rolls = copyMe.rolls.map(o => o.clone());
    this.advantage = copyMe.advantage;
    this.advantages = [...copyMe.advantages];
  }

  clone() {
    const new_obj = new CharacterRoll();
    new_obj.copy(this);
    return new_obj;
  }
} 

export interface RangeBonus {
  attack_type: AttackType, 
  attack_details: string[], // ...Weapon Keywords, Unarmed Strike, ...Spell Schools, ...Damage Types
  damage_types: (DamageType | "Any")[],
  mod_type: ModType,
  amount: number,
  char_feature: CharacterFeature | Feature
}

/**
 * This holds all the customizations characters may add to their character.
 * There are Attribute Customizations, which change things like Max HP, Ability Scores, etc.
 * There are Action Customizations, which changes things like Abilities, Spells, Weapon Attacks, etc.
 * 
 * There are also Custom Abilities, which hold what is essentially Ability floating features to add additional abilities.
 */
export class CharacterCustomizations {
  char_id = "";
  overrides: { 
    size?: SizeType,
    pb?: number,
    ac?: number,
    max_hp?: number,
    init?: number,
    STR?: number,
    DEX?: number,
    CON?: number,
    INT?: number,
    WIS?: number,
    CHA?: number,
    attunement_slots?: number, 
    speeds: { [speed_type: string]: number },
    senses: { [sense_id: string]: number },
  } = { speeds: {}, senses: {}};
  modifiers: {
    size?: SizeType,
    pb?: number,
    ac?: number,
    max_hp?: number,
    init?: number,
    STR?: number,
    DEX?: number,
    CON?: number,
    INT?: number,
    WIS?: number,
    CHA?: number,
    attunement_slots?: number, 
    speeds: { [speed_type: string]: number },
    senses: { [sense_id: string]: number },
  } = { speeds: {}, senses: {}};
  resource_overrides: {
    [type_id: string]: { [source_id: string]: { size?: number, count?: number }}
  } = {};
  resource_modifiers: {
    [type_id: string]: { [source_id: string]: { size?: number, count?: number }}
  } = {};
  prof_overrides: {
    [prof_id: string]: { 
      prof_level?: number, // Prof Bonus Mult.
      override?: number, // Flat Number to replace Stat Mod and PB. Also counters bonus.
      bonus?: number, // Flat Number to add to rolls, on top of Stat Mod and PB.
      stat_override?: AbilityScoreType // Use a different Ability Score than standard.
    }
  } = {};
  armor_training_overrides: { [armor_type_id: string]: boolean } = {}; 
  weapon_prof_overrides: { [weapon_keyword_id: string]: boolean } = {}; 
  tool_prof_overrides: { [tool_id: string]: boolean } = {}; 
  weapon_mastery_overrides: { 
    [weapon_keyword_id: string]: string // [weapon_keyword_id: string]: Standard or Weapon Mastery Property _id
  } = {}; 
  damage_multiplier_overrides: {
    [damage_type: string] : number
  } = {};
  extra_languages: string[] = [];
  removed_languages: string[] = [];
  extra_abilities: Ability[] = [];
  extra_extra_effects: ExtraEffectsFeature[] = [];
  creature_type?: CreatureType;
  spellcasting: SpellcastingCustomizations[] = [];
  spell_slot_level_progress: { [type_id: string]: { override?: number, modifier?: number } } = {};
  slots: {
    [slot_type: string]: { [level: number]: { override?: number, modifier?: number } }
  } = {};
  
  constructor(obj?: any) {
    if (obj) {
      this.char_id = obj.char_id ? obj.char_id : "";
      this.overrides = obj.overrides ? obj.overrides : { speeds: {}, senses: {} };
      this.modifiers = obj.modifiers ? obj.modifiers : { speeds: {}, senses: {} };
      if (obj.overrides && !obj.overrides.speeds) {
        this.overrides.speeds = {};
      }
      if (obj.modifiers && !obj.modifiers.speeds) {
        this.modifiers.speeds = {};
      }
      if (obj.overrides && !obj.overrides.senses) {
        this.overrides.senses = {};
      }
      if (obj.modifiers && !obj.modifiers.senses) {
        this.modifiers.senses = {};
      }
      this.resource_overrides = obj.resource_overrides ? obj.resource_overrides : {};
      this.resource_modifiers = obj.resource_modifiers ? obj.resource_modifiers : {};
      this.prof_overrides = obj.prof_overrides ? obj.prof_overrides : {};
      this.armor_training_overrides = obj.armor_training_overrides ? obj.armor_training_overrides : {};
      this.weapon_prof_overrides = obj.weapon_prof_overrides ? obj.weapon_prof_overrides : {};
      this.weapon_mastery_overrides = obj.weapon_mastery_overrides ? obj.weapon_mastery_overrides : {};
      this.tool_prof_overrides = obj.tool_prof_overrides ? obj.tool_prof_overrides : {};
      this.damage_multiplier_overrides = obj.damage_multiplier_overrides ? obj.damage_multiplier_overrides : {};
      this.extra_languages = obj.extra_languages ? obj.extra_languages : [];
      this.removed_languages = obj.removed_languages ? obj.removed_languages : [];
      this.extra_abilities = obj.extra_abilities ? obj.extra_abilities.map((o: any) => new Ability(o)) : [];
      this.extra_extra_effects = obj.extra_extra_effects ? obj.extra_extra_effects.map((o: any) => new ExtraEffectsFeature(o)) : [];
      this.creature_type = obj.creature_type ? obj.creature_type : undefined;
      this.spellcasting = obj.spellcasting ? obj.spellcasting.map((o: any) => new SpellcastingCustomizations(o)) : [];
      this.spell_slot_level_progress = obj.spell_slot_level_progress ? obj.spell_slot_level_progress : {};
      this.slots = obj.slots ? obj.slots : {};
    }
  }

  toDBObj() {
    return {
      char_id: this.char_id,
      overrides: this.overrides,
      modifiers: this.modifiers,
      resource_overrides: this.resource_overrides,
      resource_modifiers: this.resource_modifiers,
      prof_overrides: this.prof_overrides,
      armor_training_overrides: this.armor_training_overrides,
      weapon_prof_overrides: this.weapon_prof_overrides,
      weapon_mastery_overrides: this.weapon_mastery_overrides,
      tool_prof_overrides: this.tool_prof_overrides,
      damage_multiplier_overrides: this.damage_multiplier_overrides,
      extra_languages: this.extra_languages,
      removed_languages: this.removed_languages,
      extra_abilities: this.extra_abilities.map(o => o.toDBObj()),
      extra_extra_effects: this.extra_extra_effects.map(o => o.toDBObj()),
      creature_type: this.creature_type,
      spellcasting: this.spellcasting.map(o => o.toDBObj()),
      spell_slot_level_progress: this.spell_slot_level_progress,
      slots: this.slots,
    };
  }

  clone(drop_ids = false): CharacterCustomizations {
    const clone = new CharacterCustomizations();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: CharacterCustomizations, drop_ids = false): void {
    this.char_id = copyMe.char_id;
    this.overrides = {...copyMe.overrides};
    this.modifiers = {...copyMe.modifiers};
    this.resource_overrides = {...copyMe.resource_overrides};
    this.resource_modifiers = {...copyMe.resource_modifiers};
    this.prof_overrides = {...copyMe.prof_overrides};
    this.armor_training_overrides = {...copyMe.armor_training_overrides};
    this.weapon_prof_overrides = {...copyMe.weapon_prof_overrides};
    this.weapon_mastery_overrides = {...copyMe.weapon_mastery_overrides};
    this.tool_prof_overrides = {...copyMe.tool_prof_overrides};
    this.damage_multiplier_overrides = {...copyMe.damage_multiplier_overrides};
    this.extra_languages = [...copyMe.extra_languages];
    this.removed_languages = [...copyMe.removed_languages];
    this.extra_abilities = copyMe.extra_abilities.map(o => o.clone());
    this.extra_extra_effects = copyMe.extra_extra_effects.map(o => o.clone());
    this.creature_type = copyMe.creature_type;
    this.spellcasting = copyMe.spellcasting.map(o => o.clone());
    this.spell_slot_level_progress = { ...copyMe.spell_slot_level_progress };
    this.slots = {...copyMe.slots};
  }
}
export class SpellcastingCustomizations {
  source_true_id = ""; // true_id of the char_class this is modifying.
  spellcasting_ability: AbilityScoreType | undefined;
  spellcasting_type_id: string | undefined;
  spellcasting_mod: number | undefined;
  spell_attack: number | undefined;
  spell_save_dc: number | undefined;
  max_spell_level: number | undefined;
  max_cantrips_known: number | undefined;
  max_spells_prepared: number | undefined;
  spell_list_id: string | undefined;
  bonus_spells: string[] | undefined;
  spellcasting_level: "Full" | "Half" | "Third" | undefined;
  
  constructor(obj?: any) {
    if (obj) {
      this.source_true_id = obj.source_true_id ? obj.source_true_id : "";
      this.spellcasting_ability = obj.spellcasting_ability ? obj.spellcasting_ability : undefined;
      this.spellcasting_type_id = obj.spellcasting_type_id ? obj.spellcasting_type_id : undefined;
      this.spellcasting_mod = obj.spellcasting_mod ? obj.spellcasting_mod : undefined;
      this.spell_attack = obj.spell_attack ? obj.spell_attack : undefined;
      this.spell_save_dc  = obj.spell_save_dc ? obj.spell_save_dc : undefined;
      this.max_spell_level = obj.max_spell_level ? obj.max_spell_level : undefined;
      this.max_cantrips_known = obj.max_cantrips_known ? obj.max_cantrips_known : undefined;
      this.max_spells_prepared = obj.max_spells_prepared ? obj.max_spells_prepared : undefined;
      this.spell_list_id = obj.spell_list_id ? obj.spell_list_id : undefined;
      this.bonus_spells = obj.bonus_spells ? obj.bonus_spells : undefined;
      this.spellcasting_level = obj.spellcasting_level ? obj.spellcasting_level : undefined;
    }
  }

  toDBObj() {
    return {
      source_true_id: this.source_true_id,
      spellcasting_ability: this.spellcasting_ability,
      spellcasting_type_id: this.spellcasting_type_id,
      spellcasting_mod: this.spellcasting_mod,
      spell_attack: this.spell_attack,
      spell_save_dc: this.spell_save_dc,
      max_spell_level: this.max_spell_level,
      max_cantrips_known: this.max_cantrips_known,
      max_spells_prepared: this.max_spells_prepared,
      spell_list_id: this.spell_list_id,
      bonus_spells: this.bonus_spells,
      spellcasting_level: this.spellcasting_level
    };
  }

  clone(drop_ids = false): SpellcastingCustomizations {
    const clone = new SpellcastingCustomizations();
    clone.copy(this, drop_ids);
    return clone;
  }

  copy(copyMe: SpellcastingCustomizations, drop_ids = false): void {
    this.source_true_id = copyMe.source_true_id;
    this.spellcasting_ability = copyMe.spellcasting_ability;
    this.spellcasting_type_id = copyMe.spellcasting_type_id;
    this.spellcasting_mod = copyMe.spellcasting_mod;
    this.spell_attack = copyMe.spell_attack;
    this.spell_save_dc = copyMe.spell_save_dc;
    this.max_spell_level = copyMe.max_spell_level;
    this.max_cantrips_known = copyMe.max_cantrips_known;
    this.max_spells_prepared = copyMe.max_spells_prepared;
    this.spell_list_id = copyMe.spell_list_id;
    this.bonus_spells = copyMe.bonus_spells ? [...copyMe.bonus_spells] : undefined;
    this.spellcasting_level = copyMe.spellcasting_level;
  }
}